import { fetchEventSource } from "@microsoft/fetch-event-source";
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";

import React, { PropsWithChildren, useMemo, useState } from "react";
import { config } from "../../config/config.ts";
import { useFeatureFlagValue } from "../../config/FeatureFlagManager/hooks.ts";
import { getEnvironment } from "../../config/getEnvironment.ts";
import { ClaService } from "../cla/state/ClaService.ts";
import { HttpApiService } from "../insights-agent/api/HttpApiService/HttpApiService.ts";
import { InMemoryApiService } from "../insights-agent/api/InMemoryApiService.ts";
import { PollingService } from "../insights-agent/api/PollingService/PollingService.ts";
import { useAuthService_internal } from "../auth/useAuthService_internal.ts";
import { FetchHttpClient } from "../http/FetchHttpClient.ts";
import { AppContext } from "./AppContext.tsx";
import { InsightsAgentService } from "../insights-agent/state/InsightsAgentService.ts";
import { DeepResearchService } from "../deep-research/state/DeepResearchService.ts";
import { getDocumentTitle } from "../../config/getDocumentTitle.ts";
import { ProductAnalytics } from "../analytics/ProductAnalytics.ts";
import { FirebaseAnalyticsAdapter } from "../analytics/FirebaseAnalyticsAdapter.ts";
import { ProductEventTarget } from "../product-events/ProductEventTarget.ts";
import { ProductEventContext } from "../product-events/ProductEventContext.ts";

const environment = getEnvironment();
const firebaseApp = initializeApp(config.firebase[environment]);
const pollingService = new PollingService();

// Set the document title before Firebase Analytics initializes and potentially logs page_views
document.title = getDocumentTitle(config.appTitle);
const analytics = getAnalytics(firebaseApp);
const analyticsAdapter = new FirebaseAnalyticsAdapter(analytics);
const productAnalytics = new ProductAnalytics(analyticsAdapter);
const productEvents = new ProductEventTarget();
productAnalytics.subscribeToEvents(productEvents);

export const AppProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const enableFirebaseEmulator = useFeatureFlagValue("enableFirebaseEmulator");

  const { authService, isReady } = useAuthService_internal(
    firebaseApp,
    config,
    localStorage,
    enableFirebaseEmulator,
    productEvents,
  );

  const [httpClient] = useState(
    () =>
      new FetchHttpClient(
        fetch.bind(window),
        fetchEventSource,
        config.baseUrl[environment],
        authService,
      ),
  );

  const enableFakeApi = useFeatureFlagValue("enableFakeApi");
  const enableDynamicFilters = useFeatureFlagValue("enableDynamicFilters");
  const enableNewTermsAndConditionsFlow = useFeatureFlagValue(
    "enableNewTermsAndConditionsFlow",
  );
  const enableClaMode = useFeatureFlagValue("enableClaMode");
  const enableProductTestingBusinessDomain = useFeatureFlagValue(
    "enableProductTestingBusinessDomain",
  );

  const apiService = useMemo(
    () =>
      enableFakeApi
        ? new InMemoryApiService()
        : new HttpApiService(httpClient, {
            enableDynamicFilters,
            enableNewTermsAndConditionsFlow,
          }),
    [
      enableFakeApi,
      enableDynamicFilters,
      enableNewTermsAndConditionsFlow,
      httpClient,
    ],
  );

  const insightsAgentService = useMemo(() => {
    return new InsightsAgentService(
      httpClient,
      authService,
      apiService,
      pollingService,
      productEvents,
      {
        enableClaMode,
        enableProductTestingBusinessDomain,
      },
    );
  }, [
    httpClient,
    authService,
    apiService,
    enableClaMode,
    enableProductTestingBusinessDomain,
  ]);

  const deepResearchService = useMemo(() => {
    return new DeepResearchService(
      httpClient,
      authService,
      apiService,
      pollingService,
      productEvents,
      {
        enableClaMode,
        enableProductTestingBusinessDomain,
      },
    );
  }, [
    httpClient,
    authService,
    apiService,
    enableClaMode,
    enableProductTestingBusinessDomain,
  ]);

  const [claService] = useState(
    new ClaService(httpClient, productEvents, authService),
  );

  const value = useMemo(
    () => ({
      insightsAgentService,
      deepResearchService,
      config,
      authService,
      claService,
    }),
    [insightsAgentService, deepResearchService, authService, claService],
  );

  if (!isReady) {
    return null;
  }

  return (
    <ProductEventContext.Provider value={productEvents}>
      <AppContext value={value}>{children}</AppContext>
    </ProductEventContext.Provider>
  );
};
